import { useNavigate, useParams } from 'react-router-dom'
import { useBasket } from '@/contexts/basketContext.tsx'
import { Loading, Card } from 'react-daisyui'
import { Button } from '@/components/Button.tsx'
import { IconArrowBack, IconTrash, IconPlus, IconMinus, IconShoppingCart, IconEdit } from '@tabler/icons-react'
import { useState } from 'react'
import { BasketItem, UnitPriceCalculationDependsOn } from '@/models/shop.ts'
import TemplateImageWithExamples from '@/components/TemplateImageWithExamples.tsx'
import { getImageTemplate } from '@/utils/imageTemplateRegistry.ts'

export default function BasketPage() {
  const navigate = useNavigate()
  const { driveId } = useParams<{ driveId: string }>()
  const { basket, isLoading, updateItem, removeItem, clearBasket, refetch } = useBasket()
  const [isProcessing, setIsProcessing] = useState(false)

  const formatPrice = (price: string) => {
    return `€${parseFloat(price).toFixed(2)}`
  }

  // Helper function to get pricing breakdown for display
  const getPricingBreakdown = (item: BasketItem) => {
    const product = item.product
    const variation = item.variation
    const imageCount = item.units || 0

    // Get the pricing configuration following backend logic
    const priceCalculation = product.price_calculation
    const freeIncludedCount = variation?.free_included_unit_count || product.free_included_unit_count
    
    // Base price (from variation or product)
    const basePrice = parseFloat(variation?.price || product.base_price || '0')
    
    // Get unit price with fallback logic matching backend
    let unitPrice: string
    if (variation && variation.unit_price !== null) {
      unitPrice = variation.unit_price
    } else {
      // If unit_price is not set, fall back to base_price
      unitPrice = product.unit_price || (variation?.price || product.base_price || '0')
    }

    switch (priceCalculation) {
      case UnitPriceCalculationDependsOn.BASE_PRICE:
        return {
          breakdown: formatPrice(variation?.price || product.base_price || '0'),
          details: null
        }

      case UnitPriceCalculationDependsOn.PER_IMAGE: {
        const chargeableImages = Math.max(0, imageCount - freeIncludedCount)
        if (chargeableImages > 0) {
          return {
            breakdown: `${formatPrice(unitPrice)} × ${chargeableImages} Bilder`,
            details: null
          }
        } else {
          return {
            breakdown: formatPrice('0'),
            details: null
          }
        }
      }

      case UnitPriceCalculationDependsOn.BASE_PLUS_PER_IMAGE: {
        const chargeableImages = Math.max(0, imageCount - freeIncludedCount)
        if (chargeableImages > 0) {
          return {
            breakdown: `${formatPrice(basePrice.toString())} + ${formatPrice(unitPrice)} × ${chargeableImages} Bilder`,
            details: null
          }
        } else {
          return {
            breakdown: formatPrice(basePrice.toString()),
            details: null
          }
        }
      }

      default:
        return {
          breakdown: formatPrice(item.unit_price),
          details: null
        }
    }
  }

  const handleQuantityChange = async (itemId: string, newQuantity: number) => {
    if (newQuantity <= 0) {
      await handleRemoveItem(itemId)
      return
    }

    try {
      setIsProcessing(true)
      await updateItem(itemId, newQuantity)
      // Refetch to ensure prices are updated
      await refetch()
    } catch (error) {
      console.error('Error updating quantity:', error)
      alert('Fehler beim Aktualisieren der Menge')
    } finally {
      setIsProcessing(false)
    }
  }

  const handleRemoveItem = async (itemId: string) => {
    try {
      setIsProcessing(true)
      await removeItem(itemId)
      // Refetch to ensure basket totals are updated
      await refetch()
    } catch (error) {
      console.error('Error removing item:', error)
      alert('Fehler beim Entfernen des Artikels')
    } finally {
      setIsProcessing(false)
    }
  }

  const handleClearBasket = async () => {
    if (!confirm('Möchten Sie den Warenkorb wirklich leeren?')) {
      return
    }

    try {
      setIsProcessing(true)
      await clearBasket()
      // Refetch to ensure basket is empty
      await refetch()
    } catch (error) {
      console.error('Error clearing basket:', error)
      alert('Fehler beim Leeren des Warenkorbs')
    } finally {
      setIsProcessing(false)
    }
  }

  const handleCheckout = () => {
    navigate(`/d/${driveId}/shop/basket/checkout`)
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loading color="primary" />
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <Button
          color="primary"
          variant="outline"
          size="sm"
          onClick={() => navigate(`/d/${driveId}/shop`)}
        >
          <IconArrowBack size={20} />
          Zurück zum Shop
        </Button>

        <div className="flex items-center gap-4">
          <h1 className="text-2xl font-bold">
            <IconShoppingCart size={28} className="inline mr-2" />
            Warenkorb
          </h1>

          {basket && basket.items.length > 0 && (
            <Button
              color="error"
              variant="outline"
              size="sm"
              onClick={handleClearBasket}
              disabled={isProcessing}
            >
              <IconTrash size={16} />
              Leeren
            </Button>
          )}
        </div>
      </div>

      {/* Empty basket state */}
      {!basket || basket.items.length === 0 ? (
        <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4">
          <IconShoppingCart size={80} className="text-gray-400" />
          <h2 className="text-xl font-semibold text-gray-600">Ihr Warenkorb ist leer</h2>
          <p className="text-gray-500 text-center max-w-md">
            Entdecken Sie unsere Produkte und fügen Sie Artikel zu Ihrem Warenkorb hinzu.
          </p>
          <Button
            color="primary"
            size="lg"
            onClick={() => navigate(`/d/${driveId}/shop`)}
          >
            Zum Shop
          </Button>
        </div>
      ) : (
        <>
          {/* Basket items */}
          <div className="space-y-4 mb-8">
            {basket.items.map((item) => (
              <BasketItemCard
                key={item.id}
                item={item}
                onQuantityChange={handleQuantityChange}
                onRemove={handleRemoveItem}
                isProcessing={isProcessing}
                getPricingBreakdown={getPricingBreakdown}
              />
            ))}
          </div>

          {/* Basket summary */}
          <div className="bg-base-200 rounded-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Zusammenfassung</h3>
              <div className="text-sm text-gray-600">
                {basket.items_count} Artikel{basket.items_count !== 1 ? '' : ''}
              </div>
            </div>

            <div className="space-y-2 mb-4">
              <div className="flex justify-between">
                <span>Zwischensumme:</span>
                <span>{formatPrice(basket.total_price)}</span>
              </div>
              <div className="flex justify-between text-sm text-gray-600">
                <span>Versand:</span>
                <span>Wird an der Kasse berechnet</span>
              </div>
              <div className="flex justify-between text-sm text-gray-600">
                <span>MwSt.:</span>
                <span>Enthalten</span>
              </div>
              <hr className="my-2" />
              <div className="flex justify-between font-bold text-lg">
                <span>Gesamt:</span>
                <span>{formatPrice(basket.total_price)}</span>
              </div>
            </div>

            <Button
              color="primary"
              size="lg"
              className="w-full"
              onClick={handleCheckout}
              disabled={isProcessing}
            >
              Zur Kasse ({formatPrice(basket.total_price)})
            </Button>
          </div>
        </>
      )}
    </div>
  )
}

function BasketItemCard({
  item,
  onQuantityChange,
  onRemove,
  isProcessing,
  getPricingBreakdown
}: {
  item: BasketItem
  onQuantityChange: (itemId: string, quantity: number) => void
  onRemove: (itemId: string) => void
  isProcessing: boolean
  getPricingBreakdown: (item: BasketItem) => { breakdown: string; details: string | null }
}) {
  const navigate = useNavigate()
  const { driveId } = useParams<{ driveId: string }>()

  const formatPrice = (price: string) => {
    return `€${parseFloat(price).toFixed(2)}`
  }

  const getDisplayTitle = () => {
    if (item.variation) {
      return `${item.product.title} - ${item.variation.title_suffix}`
    }
    return item.product.title
  }

  const getDisplayImage = () => {
    return item.product.preview_image || '/placeholder-product.jpg'
  }

  const imageTemplate = getImageTemplate(item.product.image_template_id)

  const handleEditItem = () => {
    navigate(`/d/${driveId}/shop/basket/edit/${item.id}`)
  }

  return (
    <Card className="bg-base-100 shadow-lg">
      <div className="card-body">
        <div className="flex gap-4">
          {/* Product image */}
          <div className="flex-shrink-0">
            {imageTemplate ? (
              <TemplateImageWithExamples
                backgroundImage={getDisplayImage()}
                template={imageTemplate}
                className="w-20 h-20 rounded-lg"
              />
            ) : (
              <img
                src={getDisplayImage()}
                alt={getDisplayTitle()}
                className="w-20 h-20 object-cover rounded-lg"
              />
            )}
          </div>

          {/* Product details */}
          <div className="flex-1">
            <h3 className="font-semibold text-lg">{getDisplayTitle()}</h3>

            {item.product.description && (
              <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                {item.product.description}
              </p>
            )}

            {/* Variation info */}
            {item.variation && (
              <div className="mt-2 text-sm text-gray-500">
                SKU: {item.variation.full_sku}
              </div>
            )}

            {/* Playlist info */}
            {item.playlist && (
              <div className="mt-2 text-sm text-primary">
                📁 ({item.units || 0} Bilder)
                <Button
                  size="sm"
                  color="primary"
                  variant="outline"
                  className="ml-2"
                  onClick={handleEditItem}
                  disabled={isProcessing}
                >
                  <IconEdit size={14} />
                  Bearbeiten
                </Button>
              </div>
            )}

            {/* Mods info */}
            {item.mods && item.mods.length > 0 && (
              <div className="mt-2 space-y-1">
                {item.mods.map((mod) => (
                  <div key={mod.id} className="text-sm text-gray-600">
                    {mod.property_name}: {mod.value}
                  </div>
                ))}
              </div>
            )}

            {/* Price and quantity controls */}
            <div className="flex items-center justify-between mt-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Menge:</span>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    shape="circle"
                    onClick={() => onQuantityChange(item.id, item.quantity - 1)}
                    disabled={isProcessing || item.quantity <= 1}
                  >
                    <IconMinus size={16} />
                  </Button>
                  <span className="w-8 text-center font-medium">{item.quantity}</span>
                  <Button
                    size="sm"
                    variant="outline"
                    shape="circle"
                    onClick={() => onQuantityChange(item.id, item.quantity + 1)}
                    disabled={isProcessing}
                  >
                    <IconPlus size={16} />
                  </Button>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="text-right">
                  <div className="text-sm text-gray-600">
                    {getPricingBreakdown(item).breakdown}
                  </div>
                  {getPricingBreakdown(item).details && (
                    <div className="text-xs text-gray-500">
                      {getPricingBreakdown(item).details}
                    </div>
                  )}
                  <div className="font-semibold text-lg">
                    {formatPrice(item.total_price)}
                  </div>
                </div>

                <Button
                  size="sm"
                  color="error"
                  variant="outline"
                  onClick={() => onRemove(item.id)}
                  disabled={isProcessing}
                >
                  <IconTrash size={16} />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Card>
  )
}
